import { isSameDay } from "date-fns";
import {
  MODE_MOVE,
  MODE_RESIZE_TOP,
  MODE_RESIZE_BOTTOM,
  STATUS_DELETED,
  STATUS_UNSAVED,
  STATUS_CREATING,
} from "./actions";
import { sortBy } from "lodash";
import { ScheduleBlock, TimeFormat } from "@aika/types";
import {
  BlockWithStatus,
  CalendarMode,
  CalendarState,
  MoveAlgorithm,
} from "./types";

export const MIN_BLOCK = 15;
export const ROW_MINS = 15;

export const formatTime = (x: number, format: TimeFormat): string => {
  let hours = Math.floor(x / 60);
  let minutes = x % 60;
  if (x === 1440) {
    hours = 0;
    minutes = 0;
  }
  let suffix =
    format === TimeFormat.TwelveHour ? (hours >= 12 ? " PM" : " AM") : "";
  if (format === TimeFormat.TwelveHour && hours > 12) {
    hours = hours - 12;
  }

  return (
    hours.toString().padStart(2, "0") +
    ":" +
    minutes.toString().padStart(2, "0") +
    suffix
  );
};

export const useIsMobile = () => {
  return /Mobi/.test(window.navigator.userAgent);
};

export const getExtent = (
  item: ScheduleBlock,
  rowHeight: number,
  shownRange: number[]
) => {
  const pxPerMin = rowHeight / ROW_MINS;
  const end = (item.start - shownRange[0] + item.length) * pxPerMin;
  const start = (item.start - shownRange[0]) * pxPerMin;

  return [start, end];
};

export const getItemByY = (
  state: CalendarState,
  date: Date,
  y: number
): { item: BlockWithStatus; mode: CalendarMode } => {
  const item = state.items.find((i) => {
    const ex = getExtent(i, state.rowHeight, state.shownRange);
    return isSameDay(i.date, date) && ex[0] <= y && ex[1] > y;
  });

  if (item == null) {
    return null;
  }

  let mode: CalendarMode = MODE_MOVE;

  const ex = getExtent(item, state.rowHeight, state.shownRange);

  if (y - ex[0] < state.rowHeight / 4) {
    mode = MODE_RESIZE_TOP;
  }

  if (ex[1] - y < state.rowHeight / 4) {
    mode = MODE_RESIZE_BOTTOM;
  }

  return {
    item,
    mode,
  };
};

export const resolveItems = (
  items: BlockWithStatus[],
  activeItem: string,
  date: Date | null,
  start: number | null,
  length: number | null,
  commit: boolean,
  algo: MoveAlgorithm = "overwrite",
  endOfDay: number = 1440
): BlockWithStatus[] => {
  if (activeItem == null) {
    return items;
  }

  const item = items.find((x) => x.id === activeItem);

  if (item == null) {
    return items;
  }

  date = date == null ? item.date : date;
  start = start == null ? item.start : start;
  length = length == null ? item.length : length;
  const end = start + length;

  if (commit) {
    items = items.map((x) => ({ ...x }));

    items
      .filter((x) => x.status === STATUS_CREATING)
      .forEach((x) => (x.status = STATUS_UNSAVED));

    if (algo === "overwrite") {
      const potentialOverlaps = items.filter(
        (x) => x.id !== activeItem && isSameDay(x.date, date)
      );

      // remove
      potentialOverlaps
        .filter((x) => x.start >= start && x.start + x.length <= end)
        .forEach((x) => {
          x.status = STATUS_DELETED;
        });

      // move starts
      potentialOverlaps
        .filter(
          (x) => x.start >= start && x.start < end && x.start + x.length > end
        )
        .forEach((x) => {
          let e = x.start + x.length;
          x.start = end;
          x.length = e - end;
          x.status = STATUS_UNSAVED;
        });

      // move lengths
      potentialOverlaps
        .filter((x) => x.start < start && x.start + x.length > start)
        .forEach((x) => {
          x.length = start - x.start;
          x.status = STATUS_UNSAVED;
        });
    } else if (algo === "move") {
      const potentialOverlaps = sortBy(
        items.filter((x) => x.id !== activeItem && isSameDay(x.date, date)),
        "start"
      );

      let lastStart = end;

      for (const i of potentialOverlaps) {
        if (i.start > start && i.start < lastStart) {
          i.start = lastStart;
          lastStart += i.length;
          i.status = STATUS_UNSAVED;
        }

        if (i.start >= endOfDay || i.start < 0) {
          i.status = STATUS_DELETED;
        }

        i.length = Math.min(endOfDay - i.start, i.length);
      }
    }
  }

  const output = items.map((x) =>
    x.id !== activeItem
      ? {
          ...x,
        }
      : {
          ...x,
          date: date,
          start: start,
          length: length,
          status: commit ? STATUS_UNSAVED : x.status,
        }
  );
  return output;
};
